import React, { useEffect, useRef, useState, Suspense, forwardRef, useImperativeHandle } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { getModelPath, getFallbackImagePath } from "../constants/paths";
import { CAMERA, LIGHTS, MATERIAL, ORBIT_CONTROLS, GROUP_POSITION, ROTATION_SPEED } from "../constants/viewerConfig";

// Check if WebGL is available
function isWebGLAvailable() {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch (e) {
    return false;
  }
}

// Fallback component when WebGL is not available
function WebGLFallback({ baseId, shadeId, baseColor, shadeColor }) {
  return (
    <div className="webgl-fallback" style={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)",
      borderRadius: "12px",
      padding: "20px",
      textAlign: "center"
    }}>
      <div style={{
        position: "relative",
        width: "200px",
        height: "280px",
        marginBottom: "16px"
      }}>
        {/* Base image */}
        <img
          src={getFallbackImagePath('base', baseId)}
          alt="Lamp Base"
          style={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "180px",
            maxHeight: "140px",
            objectFit: "contain",
            filter: `drop-shadow(0 4px 8px rgba(0,0,0,0.15))`
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {/* Shade image */}
        <img
          src={getFallbackImagePath('shade', shadeId)}
          alt="Lamp Shade"
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "180px",
            maxHeight: "160px",
            objectFit: "contain",
            filter: `drop-shadow(0 4px 8px rgba(0,0,0,0.15))`
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
      <div style={{
        background: "rgba(255,255,255,0.9)",
        padding: "12px 20px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          3D Preview unavailable
        </p>
        <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#999" }}>
          Enable hardware acceleration in your browser settings
        </p>
      </div>
    </div>
  );
}

// Loading component
function LoadingFallback() {
  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)",
      borderRadius: "12px"
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "3px solid #e0e0e0",
          borderTopColor: "#3b82f6",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 12px"
        }} />
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>Loading 3D model...</p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function Part({ url, color }) {
  const { scene } = useGLTF(url);

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(color),
          roughness: MATERIAL.roughness,
          metalness: MATERIAL.metalness,
        });
      }
    });
  }, [color, scene]);

  return <primitive object={scene} />;
}

const LampViewer = forwardRef(function LampViewer({ baseId, shadeId, baseColor, shadeColor }, ref) {
  const groupRef = useRef();
  const canvasRef = useRef();
  const [webglSupported, setWebglSupported] = useState(true);
  const [hasError, setHasError] = useState(false);

  const baseUrl = getModelPath('base', baseId);
  const shadeUrl = getModelPath('shade', shadeId);

  // Expose capture method via ref
  useImperativeHandle(ref, () => ({
    captureImage: () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current.querySelector('canvas');
        if (canvas) {
          return canvas.toDataURL('image/png');
        }
      }
      return null;
    }
  }));

  // Check WebGL support on mount
  useEffect(() => {
    if (!isWebGLAvailable()) {
      setWebglSupported(false);
    }
  }, []);

  // Rotation state
  const [isDragging, setDragging] = useState(false);
  const last = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setDragging(true);
    last.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => setDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging || !groupRef.current) return;

    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };

    // Rotate around vertical axis
    groupRef.current.rotation.y += dx * ROTATION_SPEED;

    // Full 360° tilt
    groupRef.current.rotation.x += dy * ROTATION_SPEED;
  };

  // Show fallback if WebGL is not supported or there was an error
  if (!webglSupported || hasError) {
    return (
      <WebGLFallback
        baseId={baseId}
        shadeId={shadeId}
        baseColor={baseColor}
        shadeColor={shadeColor}
      />
    );
  }

  return (
    <div
      ref={canvasRef}
      style={{ width: "100%", height: "100%" }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <Canvas
        camera={{ position: CAMERA.position, fov: CAMERA.fov }}
        gl={{
          physicallyCorrectLights: true,
          colorSpace: THREE.SRGBColorSpace,
          failIfMajorPerformanceCaveat: false,
          preserveDrawingBuffer: true // Required for canvas snapshot
        }}
        onCreated={({ gl }) => {
          // Successfully created WebGL context
          console.log("WebGL context created successfully");
        }}
        onError={(error) => {
          console.error("Canvas error:", error);
          setHasError(true);
        }}
      >
        <Suspense fallback={null}>
        <ambientLight intensity={LIGHTS.ambient.intensity} />
        {LIGHTS.directional.map((light, i) => (
          <directionalLight key={i} position={light.position} intensity={light.intensity} />
        ))}

        {/* Lamp group */}
        <group ref={groupRef} position={GROUP_POSITION}>
          <Part url={baseUrl} color={baseColor} />
          <Part url={shadeUrl} color={shadeColor} />
        </group>

        {/* Zoom-only controls */}
        <OrbitControls
          enableRotate={ORBIT_CONTROLS.enableRotate}
          enablePan={ORBIT_CONTROLS.enablePan}
          minDistance={ORBIT_CONTROLS.minDistance}
          maxDistance={ORBIT_CONTROLS.maxDistance}
        />

        <Environment preset="city" />
        </Suspense>
      </Canvas>
    </div>
  );
});

export default LampViewer;
